/**
 * Merge of the two things the SPA knows about a user's organizations.
 *
 * The database is authoritative for existence, name, slug and role — the API
 * re-resolves membership and role from it on every request, and the token claim
 * is never consulted for authorization. The claim's job is to be the
 * cross-region index: it says which regions to ask, and it covers regions this
 * browser has not reached yet (ESCLOUD-268).
 */

import type {
    IParsedTenantClaim,
    IUserTenantResponse,
    IUserTenantSummary,
    TMembershipRole,
} from '@escloud/types';

export interface TenantRegistryEntry {
    tenant: IUserTenantSummary;
    role: TMembershipRole;
    joinedAt: string;
    /**
     * `server` — a regional API confirmed the membership.
     * `claim`  — only the token says so, because that tenant's region has not
     *            answered yet. Enough to navigate; the API is still the one
     *            that decides what the user may read or write.
     */
    source: 'server' | 'claim';
}

/**
 * @param claims           tenant claims parsed from the access token
 * @param serverTenants    memberships returned by every regional API we asked
 * @param answeredRegions  regions that answered — a claim for one of these is
 *                         contradicted rather than merely unconfirmed
 */
export function mergeTenantRegistry(
    claims: IParsedTenantClaim[],
    serverTenants: IUserTenantResponse[],
    answeredRegions: string[] = []
): TenantRegistryEntry[] {
    const answered = new Set(answeredRegions.map((region) => region.toLowerCase()));

    const entries = new Map<string, TenantRegistryEntry>();

    // Claims first, so a server answer overwrites them.
    for (const claim of claims) {
        // Claims are unvalidated JWT payload: an action that ever stops emitting
        // `region` must not throw inside the provider that renders the app.
        const claimRegion = String(claim.region ?? '').toLowerCase();

        // A tenant absent from its own region's answer is gone (deleted or
        // membership revoked) — the claim outlives it, so it must not resurrect it.
        if (claimRegion && answered.has(claimRegion)) continue;

        entries.set(claim.id, {
            tenant: { id: claim.id, name: claim.name, slug: claim.slug, region: claim.region },
            role: claim.role,
            joinedAt: claim.joinedAt,
            source: 'claim',
        });
    }

    for (const membership of serverTenants) {
        entries.set(membership.tenant.id, {
            tenant: membership.tenant,
            role: membership.role,
            joinedAt: membership.joinedAt,
            source: 'server',
        });
    }

    return [...entries.values()].sort((a, b) => a.joinedAt.localeCompare(b.joinedAt));
}
